/**
 * Lazy public marketing route registry and preload helpers.
 *
 * What: Maps prerendered and app-only public marketing routes to shared lazy
 * page components plus route-level preload hooks.
 * Why: Keeps route hydration aligned with the same lazy payload React renders so
 * prerendered public pages do not suspend into a fallback on first paint.
 * When: Update when public marketing routes, preload requirements, or lazy
 * loading behavior change.
 */
import type { ComponentType } from 'react';

import { isPartnershipRoutePath } from '@shared/partnerships/partnershipRoutes.js';
import {
  listSchoolSubjectPages,
  resolveSchoolSubjectRoute,
} from '@shared/schools/schoolSubjectPages.js';
import {
  listPublicMarketingRouteEntries,
  normalizePublicMarketingRoutePath,
} from '@shared/seo/publicMarketingRoutes.js';
import {
  getSubjectPage,
  isSubjectPagePath,
  SUBJECT_PAGE_PATHS,
} from '@shared/tuitionSubjects/subjectPages.js';

import type { PreloadableLazyComponent } from '../utils/lazyWithChunkRetry.js';
import { lazyWithChunkRetry } from '../utils/lazyWithChunkRetry.js';

type MarketingPageComponent = ComponentType<any>;

type MarketingImportFn = () => Promise<{
  default: MarketingPageComponent;
}>;

type MarketingRouteModule = Readonly<{
  name: string;
  routePaths: ReadonlyArray<string>;
  load: () => Promise<{ default: MarketingPageComponent }>;
  Component: PreloadableLazyComponent<MarketingPageComponent>;
}>;

type MarketingSecondaryStreamRoute = Readonly<{
  path: string;
  secLevel: string;
  stream: string;
}>;

const SERVER_MARKETING_ROUTE_PATHS = Object.freeze(
  listPublicMarketingRouteEntries()
    // Sitemap-only routes (e.g. demo pages) are not prerender targets and have
    // no marketing route module — exclude them from the prerendered set.
    .filter(({ rendering }) => rendering === 'server')
    .map(({ routePath }) => routePath)
);

const BLOG_ARTICLE_ROUTE_PREFIX = '/blog/';
const SECONDARY_STREAM_ROUTE_PATTERN = /^\/sec-([1-4])-(g1|g2|g3|ip|ib)$/;
const SCHOOL_ROUTE_PATTERN = /^\/schools\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
const AREA_ROUTE_PREFIX = '/math-tuition/';

const createMarketingRouteModule = ({
  name,
  routePaths,
  importFn,
}: {
  name: string;
  routePaths: ReadonlyArray<string>;
  importFn: MarketingImportFn;
}): MarketingRouteModule => {
  const Component = lazyWithChunkRetry(importFn);

  return Object.freeze({
    name,
    routePaths: Object.freeze([...routePaths]),
    load: () => Component.preload(),
    Component,
  });
};

const marketingRouteModules = Object.freeze({
  SchoolSubjectPage: createMarketingRouteModule({
    name: 'SchoolSubjectPage',
    routePaths: listSchoolSubjectPages().map(page => page.routePath),
    importFn: () => import('../pages/schools/SchoolSubjectPage'),
  }),
  SubjectPage: createMarketingRouteModule({
    name: 'SubjectPage',
    routePaths: SUBJECT_PAGE_PATHS,
    importFn: () => import('../pages/subjects/SubjectPage'),
  }),
  LandingPage: createMarketingRouteModule({
    name: 'LandingPage',
    routePaths: ['/'],
    importFn: () => import('../pages/LandingPage'),
  }),
  AboutUsPage: createMarketingRouteModule({
    name: 'AboutUsPage',
    routePaths: ['/about-us'],
    importFn: () => import('../pages/AboutUsPage'),
  }),
  ClassJoinPage: createMarketingRouteModule({
    name: 'ClassJoinPage',
    routePaths: ['/class'],
    importFn: () => import('../pages/ClassJoinPage'),
  }),
  EnrollPage: createMarketingRouteModule({
    name: 'EnrollPage',
    routePaths: ['/register'],
    importFn: () => import('../pages/EnrollPage'),
  }),
  SubsequentPaymentPage: createMarketingRouteModule({
    name: 'SubsequentPaymentPage',
    routePaths: ['/pay'],
    importFn: () => import('../pages/SubsequentPaymentPage'),
  }),
  TermsOfUsePage: createMarketingRouteModule({
    name: 'TermsOfUsePage',
    routePaths: ['/terms'],
    importFn: () => import('../pages/TermsOfUsePage'),
  }),
  PrivacyPolicyPage: createMarketingRouteModule({
    name: 'PrivacyPolicyPage',
    routePaths: ['/privacy'],
    importFn: () => import('../pages/PrivacyPolicyPage'),
  }),
  BlogListingPage: createMarketingRouteModule({
    name: 'BlogListingPage',
    routePaths: ['/blog'],
    importFn: () => import('../pages/blog/BlogListingPage'),
  }),
  BlogArticlePage: createMarketingRouteModule({
    name: 'BlogArticlePage',
    routePaths: SERVER_MARKETING_ROUTE_PATHS.filter(routePath =>
      routePath.startsWith(BLOG_ARTICLE_ROUTE_PREFIX)
    ),
    importFn: () => import('../pages/blog/BlogArticlePage'),
  }),
  PrimarySchoolHubPage: createMarketingRouteModule({
    name: 'PrimarySchoolHubPage',
    routePaths: ['/primary-school'],
    importFn: () => import('../pages/levels/PrimarySchoolHubPage'),
  }),
  SecondarySchoolHubPage: createMarketingRouteModule({
    name: 'SecondarySchoolHubPage',
    routePaths: ['/secondary-school'],
    importFn: () => import('../pages/levels/SecondarySchoolHubPage'),
  }),
  Primary1Page: createMarketingRouteModule({
    name: 'Primary1Page',
    routePaths: ['/primary-1'],
    importFn: () => import('../pages/levels/Primary1Page'),
  }),
  Primary2Page: createMarketingRouteModule({
    name: 'Primary2Page',
    routePaths: ['/primary-2'],
    importFn: () => import('../pages/levels/Primary2Page'),
  }),
  Primary3Page: createMarketingRouteModule({
    name: 'Primary3Page',
    routePaths: ['/primary-3'],
    importFn: () => import('../pages/levels/Primary3Page'),
  }),
  Primary4Page: createMarketingRouteModule({
    name: 'Primary4Page',
    routePaths: ['/primary-4'],
    importFn: () => import('../pages/levels/Primary4Page'),
  }),
  Primary5Page: createMarketingRouteModule({
    name: 'Primary5Page',
    routePaths: ['/primary-5'],
    importFn: () => import('../pages/levels/Primary5Page'),
  }),
  Primary6Page: createMarketingRouteModule({
    name: 'Primary6Page',
    routePaths: ['/primary-6'],
    importFn: () => import('../pages/levels/Primary6Page'),
  }),
  Sec1Page: createMarketingRouteModule({
    name: 'Sec1Page',
    routePaths: ['/sec-1'],
    importFn: () => import('../pages/levels/Sec1Page'),
  }),
  Sec2Page: createMarketingRouteModule({
    name: 'Sec2Page',
    routePaths: ['/sec-2'],
    importFn: () => import('../pages/levels/Sec2Page'),
  }),
  Sec3Page: createMarketingRouteModule({
    name: 'Sec3Page',
    routePaths: ['/sec-3'],
    importFn: () => import('../pages/levels/Sec3Page'),
  }),
  Sec4Page: createMarketingRouteModule({
    name: 'Sec4Page',
    routePaths: ['/sec-4'],
    importFn: () => import('../pages/levels/Sec4Page'),
  }),
  SecondaryStreamPage: createMarketingRouteModule({
    name: 'SecondaryStreamPage',
    routePaths: SERVER_MARKETING_ROUTE_PATHS.filter(routePath =>
      SECONDARY_STREAM_ROUTE_PATTERN.test(routePath)
    ),
    importFn: () => import('../pages/levels/SecondaryStreamPage'),
  }),
  OLevelPage: createMarketingRouteModule({
    name: 'OLevelPage',
    routePaths: ['/o-level'],
    importFn: () => import('../pages/levels/OLevelPage'),
  }),
  EMathPage: createMarketingRouteModule({
    name: 'EMathPage',
    routePaths: ['/e-math'],
    importFn: () => import('../pages/levels/EMathPage'),
  }),
  AMathPage: createMarketingRouteModule({
    name: 'AMathPage',
    routePaths: ['/a-math'],
    importFn: () => import('../pages/levels/AMathPage'),
  }),
  PSLEPage: createMarketingRouteModule({
    name: 'PSLEPage',
    routePaths: ['/psle'],
    importFn: () => import('../pages/levels/PSLEPage'),
  }),
  NLevelPage: createMarketingRouteModule({
    name: 'NLevelPage',
    routePaths: ['/n-level'],
    importFn: () => import('../pages/levels/NLevelPage'),
  }),
  IPPage: createMarketingRouteModule({
    name: 'IPPage',
    routePaths: ['/integrated-programme'],
    importFn: () => import('../pages/levels/IPPage'),
  }),
  IBPage: createMarketingRouteModule({
    name: 'IBPage',
    routePaths: ['/ib'],
    importFn: () => import('../pages/levels/IBPage'),
  }),
  ContactPage: createMarketingRouteModule({
    name: 'ContactPage',
    routePaths: ['/contact', '/contact/aeis', '/contact/overseas-maths'],
    importFn: () => import('../pages/ContactPage'),
  }),
  SchoolMathPage: createMarketingRouteModule({
    name: 'SchoolMathPage',
    routePaths: [],
    importFn: () => import('../pages/schools/SchoolMathPage'),
  }),
  OnlineMathTuitionPillar: createMarketingRouteModule({
    name: 'OnlineMathTuitionPillar',
    routePaths: ['/online-math-tuition'],
    importFn: () => import('../pages/online-tuition/OnlineMathTuitionPillar'),
  }),
  OnlinePsleMathTuition: createMarketingRouteModule({
    name: 'OnlinePsleMathTuition',
    routePaths: ['/online-psle-math-tuition'],
    importFn: () => import('../pages/online-tuition/OnlinePsleMathTuition'),
  }),
  OnlineOLevelMathTuition: createMarketingRouteModule({
    name: 'OnlineOLevelMathTuition',
    routePaths: ['/online-o-level-math-tuition'],
    importFn: () => import('../pages/online-tuition/OnlineOLevelMathTuition'),
  }),
  OnlineSecondaryMathTuition: createMarketingRouteModule({
    name: 'OnlineSecondaryMathTuition',
    routePaths: ['/online-secondary-math-tuition'],
    importFn: () =>
      import('../pages/online-tuition/OnlineSecondaryMathTuition'),
  }),
  OnlineOrInPersonMathTuition: createMarketingRouteModule({
    name: 'OnlineOrInPersonMathTuition',
    routePaths: ['/online-or-in-person-math-tuition-singapore'],
    importFn: () =>
      import('../pages/online-tuition/OnlineOrInPersonMathTuition'),
  }),
  OnlineMathTuitionOptions: createMarketingRouteModule({
    name: 'OnlineMathTuitionOptions',
    routePaths: ['/online-math-tuition-options-singapore'],
    importFn: () => import('../pages/online-tuition/OnlineMathTuitionOptions'),
  }),
  // One templated component serving all 15 `/math-tuition/<area>` routes, the
  // same shape as SecondaryStreamPage above.
  AreaMathTuitionPage: createMarketingRouteModule({
    name: 'AreaMathTuitionPage',
    routePaths: SERVER_MARKETING_ROUTE_PATHS.filter(routePath =>
      routePath.startsWith(AREA_ROUTE_PREFIX)
    ),
    importFn: () => import('../pages/areas/AreaMathTuitionPage'),
  }),
  // One templated component per partnership slug, like the two above. The
  // route paths come from the manifest rather than the slug table directly, so
  // a partner that is authored but withheld from the sitemap never gets a
  // prerender target it has no crawl entry for.
  PartnershipWelcomePage: createMarketingRouteModule({
    name: 'PartnershipWelcomePage',
    routePaths: SERVER_MARKETING_ROUTE_PATHS.filter(isPartnershipRoutePath),
    importFn: () => import('../pages/partnerships/PartnershipWelcomePage'),
  }),
  PsleMathPracticePage: createMarketingRouteModule({
    name: 'PsleMathPracticePage',
    routePaths: ['/psle-math-practice'],
    importFn: () => import('../pages/online-tuition/PsleMathPracticePage'),
  }),
  SecondaryMathSyllabusPage: createMarketingRouteModule({
    name: 'SecondaryMathSyllabusPage',
    routePaths: ['/secondary-math-syllabus'],
    importFn: () => import('../pages/levels/SecondaryMathSyllabusPage'),
  }),
  PrimaryMathSyllabusPage: createMarketingRouteModule({
    name: 'PrimaryMathSyllabusPage',
    routePaths: ['/primary-math-syllabus'],
    importFn: () => import('../pages/levels/PrimaryMathSyllabusPage'),
  }),
});

const marketingRouteModuleList = Object.freeze(
  Object.values(marketingRouteModules) as MarketingRouteModule[]
);

const marketingRouteModuleByPath = new Map<string, MarketingRouteModule>();

for (const marketingRouteModule of marketingRouteModuleList) {
  for (const routePath of marketingRouteModule.routePaths) {
    marketingRouteModuleByPath.set(routePath, marketingRouteModule);
  }
}

export const marketingLazyPages = Object.freeze(
  Object.fromEntries(
    marketingRouteModuleList.map(marketingRouteModule => [
      marketingRouteModule.name,
      marketingRouteModule.Component,
    ])
  )
) as Readonly<Record<string, PreloadableLazyComponent<MarketingPageComponent>>>;

export const marketingSecondaryStreamRoutes = Object.freeze(
  Array.from(marketingRouteModuleByPath.keys())
    .map(routePath => {
      const match = SECONDARY_STREAM_ROUTE_PATTERN.exec(routePath);

      if (!match) {
        return null;
      }

      const [, secLevel, stream] = match;

      return Object.freeze({
        path: routePath,
        secLevel,
        stream,
      });
    })
    .filter((route): route is MarketingSecondaryStreamRoute => route !== null)
);

export function preloadMarketingRouteModule(routePath: string) {
  const normalizedRoutePath = normalizePublicMarketingRoutePath(routePath);
  const marketingRouteModule =
    marketingRouteModuleByPath.get(normalizedRoutePath) ||
    (SCHOOL_ROUTE_PATTERN.test(normalizedRoutePath)
      ? marketingRouteModules.SchoolMathPage
      : null);

  if (!marketingRouteModule) {
    return Promise.resolve(null);
  }

  const schoolSubjectPage = resolveSchoolSubjectRoute(normalizedRoutePath);
  if (schoolSubjectPage) {
    return Promise.all([
      marketingRouteModule.load(),
      import('../content/subjects/loadSubjectContent').then(
        ({ preloadSubjectContent }) =>
          Promise.all(
            schoolSubjectPage.pathways.map(pathway =>
              preloadSubjectContent(pathway.contentId)
            )
          )
      ),
    ]).then(([module]) => module);
  }
  if (isSubjectPagePath(normalizedRoutePath)) {
    return Promise.all([
      marketingRouteModule.load(),
      import('../content/subjects/loadSubjectContent').then(
        ({ preloadSubjectContent }) =>
          preloadSubjectContent(getSubjectPage(normalizedRoutePath).id)
      ),
    ]).then(([module]) => module);
  }
  return marketingRouteModule.load();
}
